/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 * See the LICENSE file for the full text.
 */

/**
 * Front-of-house validation for the event wizard.
 *
 * None of this is a security boundary — the platform re-validates every column,
 * every permission and every constraint server-side. Its job is to catch a
 * mistake while the admin can still see the field that caused it, rather than
 * after a rejected batch.
 */

/**
 * Mirrors the `cap_activities_per_event` row limit declared on `event_activity`
 * (limit_value 10, scoped by event_id). Kept in step with the table's own
 * declaration; the platform is still the authority.
 */
export const MAX_ACTIVITIES_PER_EVENT = 10;

/**
 * The screen limit on the two participant-facing free-text columns, mirroring
 * `validation_rules.max_length` on `event.prerequisites` and
 * `event.preparation_steps`. Both are unbounded `text` in the database and the
 * platform stores that rule without enforcing it, so this form is the only
 * place the limit is actually applied. The textarea's own `maxLength` stops
 * typing at the cap; this check is what catches a value that arrived some other
 * way — an event created before the limit existed, loaded back into Edit.
 */
export const MAX_PARTICIPANT_NOTE_LENGTH = 2048;

/** Free-text columns the cap applies to, with the name to use in the message. */
const CAPPED_EVENT_FIELDS = {
  prerequisites: 'Prerequisites',
  preparation_steps: 'Preparation steps',
};

const REQUIRED_EVENT_FIELDS = {
  title: 'Give the event a title.',
  description: 'Describe the event.',
  // NOT NULL on the column. Without this the omission surfaces only as a
  // rejected batch, on a screen the admin has already left.
  cover_image_url: 'Upload a cover image.',
  creator_type: 'Say who is authoring this event.',
  organizer: 'Say who is hosting the event.',
  activity_mode: 'Choose how the activities run.',
  location: 'Enter the location.',
  location_state: 'Enter the state.',
  location_country: 'Enter the country.',
  timezone: 'Choose the timezone the event runs in.',
  start_datetime: 'Enter when the event starts.',
  end_datetime: 'Enter when the event ends.',
  registration_source: 'Choose where people register.',
  registration_type_id: 'Choose a registration type.',
};

const isBlank = (value) => value === undefined || value === null || value === '';

/** Parses a datetime-local or ISO string. Returns null when unusable. */
function moment(value) {
  if (isBlank(value)) return null;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function hasPoint(point) {
  return !!point
    && point.lat !== undefined && point.lat !== null && point.lat !== ''
    && point.lng !== undefined && point.lng !== null && point.lng !== '';
}

function validateActivity(activity) {
  const errors = {};

  if (isBlank(activity.name)) errors.name = 'Name this activity.';
  if (isBlank(activity.activity_type_id)) errors.activity_type_id = 'Choose an activity type.';
  if (isBlank(activity.location_model)) {
    errors.location_model = 'Choose whether this activity follows a route or sits at a venue.';
    return errors;
  }

  if (activity.location_model === 'ROUTE_BASED') {
    const route = activity.route;
    if (!route || !hasPoint(route.start_point) || !hasPoint(route.end_point)) {
      errors.route = 'A route activity needs start and end coordinates.';
    } else if (isBlank(route.start_address) || isBlank(route.end_address)) {
      errors.route = 'A route activity needs a start and end address.';
    } else if (isBlank(route.total_distance) || Number(route.total_distance) <= 0) {
      errors.route = 'Enter the total distance of the route.';
    } else if (isBlank(route.distance_measuring_unit_id)) {
      errors.route = 'Choose the unit the distance is measured in.';
    }
    (activity.pit_stops || []).forEach((stop, index) => {
      if (!hasPoint(stop.point) || isBlank(stop.name)) {
        errors.pit_stops = `Pit stop ${index + 1} needs a name and coordinates.`;
      }
    });
  }

  if (activity.location_model === 'VENUE_BASED') {
    const venue = activity.venue;
    if (!venue || isBlank(venue.venue_name) || isBlank(venue.venue_address)) {
      errors.venue = 'A venue activity needs a venue name and address.';
    } else if (!hasPoint(venue.venue_point)) {
      errors.venue = 'Enter the venue coordinates.';
    }
  }

  return errors;
}

export function validateEventForm(form) {
  const errors = {};

  Object.entries(REQUIRED_EVENT_FIELDS).forEach(([field, message]) => {
    if (isBlank(form[field])) errors[field] = message;
  });

  // Mirrors chk_event_creator_xor on the event table: exactly one of
  // creator_organization_id / creator_user_id must be set. Without this the
  // batch is refused by the database with a constraint error the admin cannot
  // act on.
  if (form.creator_type === 'ORGANIZATION' && isBlank(form.creator_organization_id)) {
    errors.creator_organization_id = 'Choose the organization hosting this event.';
  }
  if (form.creator_type === 'USER' && isBlank(form.creator_user_id)) {
    errors.creator_user_id = 'Enter the id of the user authoring this event.';
  }

  Object.entries(CAPPED_EVENT_FIELDS).forEach(([field, label]) => {
    const length = (form[field] || '').length;
    if (length > MAX_PARTICIPANT_NOTE_LENGTH) {
      errors[field] = `${label} must be ${MAX_PARTICIPANT_NOTE_LENGTH} characters or fewer `
        + `— it is currently ${length}.`;
    }
  });

  // ── Event window ──────────────────────────────────────────────────────────
  const startsAt = moment(form.start_datetime);
  const endsAt = moment(form.end_datetime);
  if (startsAt && endsAt && endsAt <= startsAt) {
    errors.end_datetime = 'The event must end after it starts.';
  }

  // ── Activities ────────────────────────────────────────────────────────────
  const activities = form.activities || [];
  if (!activities.length) {
    errors.activities = 'Add at least one activity.';
  } else if (activities.length > MAX_ACTIVITIES_PER_EVENT) {
    errors.activities = `An event can have at most ${MAX_ACTIVITIES_PER_EVENT} activities.`;
  }

  const activityErrors = activities.map(validateActivity);
  if (activityErrors.some((entry) => Object.keys(entry).length)) {
    errors.activityErrors = activityErrors;
  }

  // ── Registration ──────────────────────────────────────────────────────────
  if (form.registration_source === 'PLATFORM') {
    const registration = form.platform_registration || {};
    const opensAt = moment(registration.registration_start_datetime);
    const closesAt = moment(registration.registration_end_datetime);

    if (!opensAt) {
      errors.registration_start_datetime = 'Enter when registration opens.';
    }
    if (!closesAt) {
      errors.registration_end_datetime = 'Enter when registration closes.';
    } else if (opensAt && closesAt <= opensAt) {
      errors.registration_end_datetime = 'Registration must close after it opens.';
    } else if (startsAt && closesAt > startsAt) {
      errors.registration_end_datetime = 'Registration must close before the event starts.';
    }

    const capacity = registration.max_participants;
    if (!isBlank(capacity) && (!Number.isFinite(Number(capacity)) || Number(capacity) <= 0)) {
      errors.max_participants = 'Capacity must be a positive number, or left empty for unlimited.';
    }
  }

  if (form.registration_source === 'EXTERNAL') {
    const external = form.external_registration || {};
    if (isBlank(external.external_url)) {
      errors.external_url = 'Enter the URL people register on.';
    }
  }

  return errors;
}
